// ═══════════════════════════════════════════════════════════════════════════════
// System message list — subject + date; modal for HTML detail
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { SystemMessage } from '@fdh/shared';
import { useLanguage } from '../../contexts/LanguageContext';
import { systemMessageApi } from '../../lib/api';
import { SystemMessageDetailModal } from '../../components/systemMessage/SystemMessageTicker';

export const SystemMessageListPage = () => {
  const { t, language } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<SystemMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<SystemMessage | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await systemMessageApi.list(language);
      if (!res.success || !res.data) {
        setError(res.error ?? t('systemMessage.error.loadList'));
        setItems([]);
        return;
      }
      setItems(res.data.items);
    } catch {
      setError(t('systemMessage.error.loadList'));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [language, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleOpen = useCallback(
    async (item: SystemMessage) => {
      setSelected(item);
      if (!item.isRead) {
        try {
          await systemMessageApi.markRead(item.id);
          setItems((prev) =>
            prev.map((row) => (row.id === item.id ? { ...row, isRead: true } : row)),
          );
        } catch {
          // Keep modal usable even if read mark fails.
        }
      }
    },
    [],
  );

  useEffect(() => {
    const id = searchParams.get('id');
    if (!id || items.length === 0) {
      return;
    }
    const target = items.find((item) => item.id === id);
    if (!target) {
      return;
    }
    void handleOpen(target);
    const next = new URLSearchParams(searchParams);
    next.delete('id');
    setSearchParams(next, { replace: true });
  }, [handleOpen, items, searchParams, setSearchParams]);

  return (
    <main className="px-4 py-6">
      <h1 className="mb-4 text-xl font-bold text-gray-900 dark:text-gray-100">
        {t('systemMessage.list.title')}
      </h1>

      {loading ? (
        <div className="flex justify-center py-16" role="status" aria-label={t('common.loading')}>
          <div className="h-9 w-9 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
        </div>
      ) : null}

      {!loading && error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-center text-sm text-red-700" role="alert">
          {error}
          <button
            type="button"
            onClick={() => void load()}
            className="mt-2 block w-full text-sm font-semibold text-primary-700 underline"
          >
            {t('task.action.refresh')}
          </button>
        </div>
      ) : null}

      {!loading && !error && items.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">{t('systemMessage.list.empty')}</p>
      ) : null}

      {!loading && !error && items.length > 0 ? (
        <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
          <table className="min-w-full divide-y divide-gray-200 text-left text-sm dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th scope="col" className="px-3 py-2.5 font-semibold text-gray-700 dark:text-gray-200">
                  {t('systemMessage.list.colSubject')}
                </th>
                <th
                  scope="col"
                  className="w-28 px-3 py-2.5 font-semibold text-gray-700 dark:text-gray-200"
                >
                  {t('systemMessage.list.colDate')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white dark:divide-gray-700 dark:bg-gray-900">
              {items.map((item) => (
                <tr key={item.id}>
                  <td className="px-3 py-2.5">
                    <button
                      type="button"
                      onClick={() => void handleOpen(item)}
                      className={[
                        'w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                        item.isRead
                          ? 'font-normal text-gray-700 dark:text-gray-300'
                          : 'font-semibold text-gray-900 dark:text-gray-100',
                      ].join(' ')}
                      aria-label={t('systemMessage.list.openItem', { subject: item.subject })}
                      tabIndex={0}
                    >
                      {!item.isRead ? (
                        <span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-primary-500" aria-hidden />
                      ) : null}
                      {item.subject}
                    </button>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5 text-gray-500 dark:text-gray-400">
                    {item.listDate}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {selected ? (
        <SystemMessageDetailModal item={selected} onClose={() => setSelected(null)} />
      ) : null}
    </main>
  );
};
